import { faCircleChevronLeft, faCircleChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CircularProgress, Divider, IconButton, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { capitalize } from "app/tools/common"
import {
  ISspMediaApprovalsByMedia,
  ISspMediaApprovalShort,
  MEDIA_APPROVAL_STATUSES,
  MEDIA_ERROR_URL,
  MEDIA_SIMPLE_TYPES
} from "media/model"
import { mediaRPO } from "media/repository"
import { getMediaType } from "media/tools/common"
import { getMediaApprovalButtons, getMediaApprovalInfos, getMediaVideos } from "media/tools/media-approval"
import React from "react"
import { connect } from "react-redux"
import { getSspNameFromId } from "ssp/tools/common"
import { FlexBox } from "ui/components/FlexBox"
import { GroupButtons } from "ui/components/GroupButtons"
import { InfoRow } from "ui/components/InfoRow"
import { LinearLoader } from "ui/components/LinearLoader"
import { TestHiddenData } from "ui/components/TestHiddenData"
import { isPhoneScreen } from "ui/tools/responsive"
import { MediaApprovalStatus } from "./MediaApprovalStatus"
import { MediaDisplay } from "./MediaDisplay"

const PARENT_DOM_ID: string = "media-approval-display"

interface IMediaApprovalDisplayConnectProps {
  isPhone: boolean
}

interface IMediaApprovalDisplayProps {
  mediaApprovalsByMedias: ISspMediaApprovalsByMedia[] // if set mediaId and creativeId will be ignored
  mediaApprovalsByMediasDefaultIndex: number
  sx?: any
  onMediaSelection: (newMediaApprovalsByMedia: ISspMediaApprovalsByMedia) => any
}

const MediaApprovalDisplayFn = React.memo(
  ({
    isPhone,
    sx,
    mediaApprovalsByMedias,
    mediaApprovalsByMediasDefaultIndex,
    onMediaSelection
  }: IMediaApprovalDisplayProps & IMediaApprovalDisplayConnectProps) => {
    const [mediaApprovalsIndex, setMediaApprovalsIndex] = React.useState(mediaApprovalsByMediasDefaultIndex || 0)
    const [mediaApprovalsByMedia, setMediaApprovalsByMedia] = React.useState<ISspMediaApprovalsByMedia | null>(null)
    const [isUpdating, setIsUpdating] = React.useState(false)
    const [isLoading, setIsLoading] = React.useState(false)
    const [hasLoadingError, setHasLoadingError] = React.useState(false)
    const [url, setUrl] = React.useState("")
    const type = mediaApprovalsByMedia ? getMediaType(mediaApprovalsByMedia.media) : MEDIA_SIMPLE_TYPES.IMAGE
    React.useEffect(() => {
      if (!mediaApprovalsByMedias || mediaApprovalsByMedias.length === 0) {
        return
      }
      const newMediaApprovalsByMedia = mediaApprovalsByMedias[mediaApprovalsIndex]
      setMediaApprovalsByMedia(newMediaApprovalsByMedia)
      let newSelectedMediaApproval: ISspMediaApprovalShort | undefined = undefined
      if (newMediaApprovalsByMedia.ssp_approval.length > 0) {
        newSelectedMediaApproval = newMediaApprovalsByMedia.ssp_approval[0]
      }
      if (!newSelectedMediaApproval) {
        return
      }
      setIsLoading(true)
      if (getMediaType(newMediaApprovalsByMedia.media) === "image") {
        let newUrl = mediaRPO.getMediaImageDownloadUrl(newMediaApprovalsByMedia.media_id)
        if (!newMediaApprovalsByMedia.media.media) {
          newUrl = MEDIA_ERROR_URL
        } else if (
          newMediaApprovalsByMedia.media.media.startsWith("http://") ||
          newMediaApprovalsByMedia.media.media.startsWith("https://")
        ) {
          newUrl = newMediaApprovalsByMedia.media.media
        }
        setUrl(newUrl)
        setIsLoading(false)
      } else {
        getMediaVideos(newMediaApprovalsByMedia.media_id)
          .then(videos => {
            if (videos.length > 0) {
              const video = videos[0]
              let newUrl = mediaRPO.getMediaVideoDownloadUrl(newMediaApprovalsByMedia.media_id, video.id)
              if (!video.file_path) {
                newUrl = MEDIA_ERROR_URL
              } else if (video.file_path.startsWith("http://") || video.file_path.startsWith("https://")) {
                newUrl = video.file_path
              }
              setUrl(newUrl)
            } else {
              setUrl(MEDIA_ERROR_URL)
            }
          })
          .catch(error => {
            console.error("Unable to get media videos", error)
            setHasLoadingError(true)
          })
          .finally(() => setIsLoading(false))
      }
    }, [mediaApprovalsByMedias, mediaApprovalsIndex])
    return (
      <FlexBox ai="start" vertical fullWidth sx={{ ...sx }}>
        {hasLoadingError && type === "video" ? (
          <Typography id="media-approval-video-error" color="error" fontSize=".9rem">
            {translate("campaign.creative.video.error")}
          </Typography>
        ) : hasLoadingError && type === "image" ? (
          <Typography id="media-approval-image-error" color="error" fontSize=".9rem">
            {translate("campaign.creative.image.error")}
          </Typography>
        ) : url === MEDIA_ERROR_URL ? (
          <Typography id="media-approval-file-missing" color="error" fontSize=".9rem">
            {translate("media.missing.file")}
          </Typography>
        ) : isLoading || !url ? (
          <LinearLoader id="media-approval-media-is-loading" />
        ) : (
          <FlexBox fullWidth jc="space-between" wrap={isPhone} ai="start" gap id={PARENT_DOM_ID}>
            <MediaDisplay
              mediaUrl={url}
              parentDomId={PARENT_DOM_ID}
              type={type}
              videoHeight="80%"
              sx={{
                maxHeight: "calc(100% - 2rem)",
                ...sx
              }}
            />
            {mediaApprovalsByMedia && (
              <FlexBox
                vertical
                fullWidth
                fullHeight
                gap=".3rem"
                jc="start"
                ai="start"
                sx={{
                  ".info-row-title": {
                    width: "8rem"
                  },
                  "& .MuiDivider-root": {
                    mt: ".5rem",
                    mb: ".5rem"
                  }
                }}>
                <FlexBox fullWidth jc="space-between">
                  <IconButton
                    id="media-approval-previous-button"
                    size="large"
                    disabled={mediaApprovalsIndex === 0}
                    onClick={() => {
                      const newIndex = mediaApprovalsIndex - 1
                      setMediaApprovalsIndex(newIndex)
                      onMediaSelection(mediaApprovalsByMedias[newIndex])
                    }}>
                    <FontAwesomeIcon icon={faCircleChevronLeft} />
                  </IconButton>
                  <Typography
                    id="media-approval-name"
                    color="textPrimary"
                    fontSize=".9rem"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "calc(100% - 7rem)"
                    }}>
                    {mediaApprovalsByMedia.media.name}
                  </Typography>
                  <IconButton
                    id="media-approval-next-button"
                    size="large"
                    disabled={mediaApprovalsIndex === mediaApprovalsByMedias.length - 1}
                    onClick={() => {
                      const newIndex = mediaApprovalsIndex + 1
                      setMediaApprovalsIndex(newIndex)
                      onMediaSelection(mediaApprovalsByMedias[newIndex])
                    }}>
                    <FontAwesomeIcon icon={faCircleChevronRight} />
                  </IconButton>
                </FlexBox>
                {getMediaApprovalInfos(mediaApprovalsByMedia).map((i, index) => (
                  <InfoRow
                    id={`media-approval-info-${i.label.replace(" ", "-").toLowerCase()}`}
                    key={index}
                    title={i.label}
                    value={i.value}
                  />
                ))}
                <Divider sx={{ width: "100%" }} />
                {mediaApprovalsByMedia.ssp_approval.map((a, index) => {
                  return (
                    <>
                      <FlexBox fullWidth gap jc="space-between">
                        <FlexBox vertical ai="start" jc="start">
                          <FlexBox gap=".4rem">
                            <Typography color="textPrimary" fontSize=".8rem">
                              {translate("ssp.title")}:
                            </Typography>
                            <Typography
                              id={`media-approval-ssp-approval-${index}-name`}
                              color="textSecondary"
                              fontSize=".8rem"
                              sx={{ maxWidth: "8rem" }}>
                              {capitalize(getSspNameFromId(a.ssp_id))}
                            </Typography>
                          </FlexBox>
                          <FlexBox gap=".4rem">
                            <Typography color="textPrimary" fontSize=".8rem">
                              {translate("label.category")}:
                            </Typography>
                            <Typography
                              id={`media-approval-ssp-approval-${index}-category`}
                              color="textSecondary"
                              fontSize=".8rem"
                              sx={{ maxWidth: "8rem" }}>
                              {a.category}
                            </Typography>
                          </FlexBox>
                          <FlexBox gap=".4rem">
                            <Typography color="textPrimary" fontSize=".8rem">
                              {translate("label.advertiser")}:
                            </Typography>
                            <Typography
                              id={`media-approval-ssp-approval-${index}-advertiser-name`}
                              color="textSecondary"
                              fontSize=".8rem"
                              sx={{ maxWidth: "8rem" }}>
                              {a.advertiser_name}
                            </Typography>
                          </FlexBox>
                        </FlexBox>
                        <FlexBox vertical gap=".3rem" ai="start">
                          {isUpdating ? (
                            <CircularProgress id={`media-approval-ssp-approval-${index}-is-updating`} size={"1.8rem"} />
                          ) : (
                            <MediaApprovalStatus
                              id={`media-approval-ssp-approval-${index}-status`}
                              status={a.approval_status_id as MEDIA_APPROVAL_STATUSES}
                            />
                          )}
                          <GroupButtons
                            id={`media-approval-grouped-button-${index}`}
                            buttons={getMediaApprovalButtons(
                              {
                                ...a,
                                media: mediaApprovalsByMedia.media,
                                media_id: mediaApprovalsByMedia.media_id,
                                approval_status_id: a.approval_status_id as MEDIA_APPROVAL_STATUSES
                              },
                              setIsUpdating
                            )}
                            allDisabled={isUpdating}
                          />
                        </FlexBox>
                      </FlexBox>
                      {index !== mediaApprovalsByMedia.ssp_approval.length - 1 && <Divider sx={{ width: "100%" }} />}
                    </>
                  )
                })}
                {mediaApprovalsByMedia && (
                  <TestHiddenData id="media-id" data={`media-id-${mediaApprovalsByMedia.media.id}`} />
                )}
              </FlexBox>
            )}
          </FlexBox>
        )}
      </FlexBox>
    )
  }
)

export const MediaApprovalDisplay = connect(
  (state: IRootState): IMediaApprovalDisplayConnectProps => ({
    isPhone: isPhoneScreen(state)
  })
)(MediaApprovalDisplayFn)
